import { ICalendarDimensions } from '../containers/BookingCalendar/Calendar/Calendar'
import {
  borders as defaultBorders,
  colors as defaultColors,
} from '../models/Event/eventConfig'
import { IBookingCalendarStore } from '../reducers/bookingCalendar'
import { theme } from '../theme'
import drawDiamond from './drawDiamond'
import {
  drawOriginIcon,
  getOriginIconLayout,
  isSingleDayEvent,
} from './drawOriginIcon'
import roundedRect from './roundedRect'
import { nbspSpace, trimName } from './text/trimName'
import wrapEventLabel from './text/wrapEventLabel'

export default function drawEvent(
  ctx: CanvasRenderingContext2D,
  constants: ICalendarDimensions,
  x: number,
  y: number,
  width: number,
  height: number,
  shape: string,
  colors,
  event,
  eventLabels: IBookingCalendarStore['eventLabels'],
  plus = false,
  doLabel = null,
  isPrice = false,
  showOriginIcon = false,
) {
  const resolvedLabel = !plus && doLabel ? doLabel(event) : ''
  const isLabelEmpty =
    showOriginIcon && !nbspSpace(String(resolvedLabel).trim())
  const iconCentered =
    showOriginIcon && (isLabelEmpty || isSingleDayEvent(event))

  const originIcon = getOriginIconLayout(
    constants,
    shape,
    x,
    y,
    width,
    height,
    event,
    !plus && showOriginIcon,
    iconCentered,
  )
  const { iconOnly, inset: iconInset } = originIcon
  const textPadding = iconInset > 0 ? 2 : 0
  const textMaxWidth =
    width - constants.EVENT_DIAMOND_SLOPE / 2 - iconInset - textPadding

  const getLines = label => {
    const trimmedLabel = nbspSpace(label.trim())
    if (trimmedLabel !== '') {
      if (textMaxWidth <= constants.GRID_WIDTH - constants.EVENT_GAP) {
        const { isTrimmed, label: trimmed } = trimName(trimmedLabel)

        if (isTrimmed) {
          return [trimmed]
        }
      }

      return wrapEventLabel(ctx, trimmedLabel, textMaxWidth, 2)
    } else {
      return []
    }
  }

  let returnedLabel = null
  let borderColor = ''

  if (!plus && !showOriginIcon) {
    if (event.bookingId && event.bookingId !== '0') {
      borderColor = defaultBorders.get('booking')
    }

    if (event.airbnbId && event.airbnbId !== '0') {
      borderColor = defaultBorders.get('airbnb')
    }

    if (event.iCalendarId && event.iCalendarId !== '0') {
      borderColor = defaultBorders.get('iCalendar')
    }

    if (event.from_website && event.from_website !== '0') {
      borderColor = defaultBorders.get('BBEngine')
    }
  }

  if (!plus) {
    let backgroundColor = isPrice ? 'rgba(0,0,0, 0.15)' : 'rgb(255,255,255)'

    if (event.stage !== 'white' && !isPrice) {
      backgroundColor = defaultColors.get(event.stage)
    }
    if (undefined !== colors && event.stage in colors && !isPrice) {
      if (!event.customColor) {
        backgroundColor = colors[event.stage]
      } else {
        backgroundColor = event.customColor
      }
    }

    ctx.fillStyle = backgroundColor
    if (borderColor !== '') {
      ctx.strokeStyle = borderColor
      ctx.lineWidth = constants.EVENT_STROKE_WIDTH
    }
  } else {
    ctx.fillStyle = theme.colors.palette.white
  }

  if ('calendarDrawEvent' in window && !plus) {
    ;(window as any).calendarDrawEvent(
      { x, y, width, height },
      shape,
      colors,
      event,
      event && event.id ? eventLabels[event.id] : undefined,
    )
  }

  if (shape === 'diamond') {
    drawDiamond(
      ctx,
      x,
      y,
      width,
      height,
      constants.EVENT_DIAMOND_SLOPE,
      borderColor !== '',
    )
  } else {
    roundedRect(
      ctx,
      x,
      y,
      width,
      height,
      {
        lb: constants.EVENT_RECT_ROUND,
        lt: constants.EVENT_RECT_ROUND,
        rb: constants.EVENT_RECT_ROUND,
        rt: constants.EVENT_RECT_ROUND,
      },
      borderColor !== '',
    )
  }

  ctx.font = `600 ${constants.EVENT_FONT_SIZE}px Montserrat`
  ctx.fillStyle = `#000000`

  const textX =
    iconInset > 0
      ? x + iconInset + textPadding
      : shape === 'diamond'
      ? x + (width + constants.EVENT_DIAMOND_SLOPE) / 2
      : x + width / 2
  ctx.textAlign = iconInset > 0 ? 'left' : 'center'

  let lines = []

  if (!plus) {
    if (iconOnly) {
      lines = []
    } else if (eventLabels[event.id] !== undefined) {
      lines = eventLabels[event.id]
    } else if (doLabel) {
      lines = getLines(resolvedLabel).map(line =>
        iconInset > 0 ? line.trim() : line,
      )

      returnedLabel = {
        [event.id]: lines,
      }
    }
  } else {
    lines = ['+']
  }

  drawOriginIcon(ctx, originIcon)

  if (lines.length) {
    if (lines.length === 1) {
      ctx.textBaseline = 'middle'
      ctx.fillText(lines[0], textX, y + height / 2)
    } else {
      ctx.textBaseline = 'bottom'
      ctx.fillText(lines[0], textX, y + height / 2)
      ctx.textBaseline = 'top'
      ctx.fillText(lines[1], textX, y + height / 2)
    }
  }

  return returnedLabel
}
